import { BaseModuleInfo, BaseAssignmentInfo, ModuleOption, Curriculum } from "../learning-center-types";
import { Transform, Type } from "class-transformer";
import { dateTimeFromSql, Truth } from "../common/common-types";
import { DateTime, Duration } from "luxon";
import { TokenInventoryData } from "../tokens/inventory/token-inventory";

export interface GetTrainingData
{
  curriculumAssignments: CurriculumAssignment[];
  moduleAssignments: HomeAssignment[];
  userId: number;
}

export class CurriculumAssignment extends Curriculum {
  id: number;
  curriculumId: number;
  userId: number;
  created: DateTime;
  modified: DateTime;
  available: DateTime | null = null;
  due: DateTime | null = null;
  completed: DateTime | null = null;
  isComplete: boolean | null = null;
  completionData: any = null; // TODO: Define this type
  active: number = 1;
  networkId: number;
}

export class HomeAssignment implements StudentTrainingData {
  Course: StudentTrainingCourseData;
  @Type(() => StudentTrainingAssignment) Assignment: StudentTrainingAssignment;
  tokens: object;

  isInCurriculum: boolean = false;
  isInVisibleCurriculum: boolean = false;

  constructor() { }

  public get isDemo(): boolean {
    return this.tokens['user_level'] === 998;
  }

  public isOverdue(): boolean {
    let today: DateTime = DateTime.fromJSDate(new Date());

    return this.Assignment.due_date.year != 0 && this.Assignment.due_date < today;
  }

  public hasDueDate(): boolean {
    return this.Assignment.due_date.year != 0;
  }

  public hideModule(): boolean {
    return this.Assignment.hideCourse ? true : false;
  }

  public daysOverdue(): number {
    return Math.round(this.Assignment.due_date.diffNow().as('days'));
  }

  public isAvailable(): boolean {
    return !this.Assignment.course_available_date.isValid || this.Assignment.course_available_date.diffNow().as('days') <= 0;
  }

  public getMinTimeLeft(): string {
    let remainingTime: number = this.Course.minimum_required_course_time_seconds - this.Assignment.spent_time_seconds;
    if (remainingTime < 60 && remainingTime > 0) remainingTime = 60;

    if (this.Course.minimum_required_course_time_seconds && remainingTime > 0) {
      let timeLeft: Duration = Duration.fromMillis(remainingTime * 1000);

      return timeLeft.toFormat('hh:mm');
    }

    return '';
  }

  public isWaitingForPractical(): string {
    if (this.Course.practical && !this.Assignment.practical && this.Assignment.status && this.Assignment.online_completion_date.year != 0) return 'Waiting for Practical Portion';
    return '';
  }

  public exceededMaxTime(): string {
    if (this.Course.maximum_course_time && this.Course.maximum_course_time_seconds > 0 && this.Assignment.spent_time_seconds >= (this.Assignment.max_time_extension + this.Course.maximum_course_time_seconds)) return 'Max Time Exceeded';
    return '';
  }

  public get expiryDate(): DateTime {
    let today: DateTime = DateTime.local();
    let expiry: DateTime = today;

    if (this.Course.expires) {
      expiry = this.Assignment.course_end_date;
  
      let retainedExpiry: DateTime | null = null;

      if (this.Course.auto_assign_retain_month) {
  
        if (this.Course.expiration_period > 0 && this.Course.expiration_period_type != '') {
          if (this.Assignment.retained_expiry?.isValid && this.Assignment.retained_expiry.year != 0) {
            // Even if the assignment does not have retained_expiry, we are calculating this from backend, so no need to recalculate the year.
            // Calculating the expiry is to get local date
            let assignmentRetained = this.Assignment.retained_expiry;
            const newDate = DateTime.local(
              assignmentRetained.year,
              assignmentRetained.month,  // Use month from retained_expiry, not from expiry
              assignmentRetained.day,    // Use day from retained_expiry, not from expiry
              0, 0
            );
            retainedExpiry = newDate;
          } else {
            // Fix: Use Luxon's plus() method for months instead of Duration.plus()
            if (this.Course.expiration_period_type === 'M') {
              // For months, use plus() method which maintains day of month
              retainedExpiry = this.Assignment.course_end_date.plus({ months: this.Course.expiration_period });
            } else {
              // For other period types, use Duration approach
              let expiryDuration: Duration = Duration.fromISO('P' + this.Course.expiration_period.toString() + this.Course.expiration_period_type);
              retainedExpiry = this.Assignment.course_end_date.plus(expiryDuration);
            }
          }
        }
      }

      if (retainedExpiry) {
        expiry = retainedExpiry;
      } else if (this.Course.expiration_period > 0 && this.Course.expiration_period_type != '') {
        // Fix: Use Luxon's plus() method for months instead of Duration.plus()
        if (this.Course.expiration_period_type === 'M') {
          // For months, use plus() method which maintains day of month
          // Add to the course_end_date, not to the current expiry value
          expiry = this.Assignment.course_end_date.plus({ months: this.Course.expiration_period });
        } else {
          // For other period types, use Duration approach
          let expiryDuration = Duration.fromISO('P' + this.Course.expiration_period.toString() + this.Course.expiration_period_type);
          expiry = this.Assignment.course_end_date.plus(expiryDuration);
        }
      }
    }

    return expiry;
  }

  public get isValidUntil(): boolean {
    return !!this.Course.use_valid_until && !!this.Course.expires;
  }

  public getBaseAssignmentData(): BaseAssignmentInfo {
    return {
      max_time_extension: this.Assignment.max_time_extension,
      practical: this.Assignment.practical,
      status: this.Assignment.status,
      calc_percent_complete: this.Assignment.calc_percent_complete,
      spent_time_seconds: this.Assignment.spent_time_seconds,
      userID: this.Assignment.userID,

      date_assigned: this.Assignment.date_assigned,
      due_date: this.Assignment.due_date,
      course_start_date: this.Assignment.course_start_date,
      course_end_date: this.Assignment.course_end_date,
      course_available_date: this.Assignment.course_available_date,
      online_completion_date: this.Assignment.online_completion_date,
      retained_expiry: this.Assignment.retained_expiry,
      expiryDate: this.isValidUntil ? applyValidPeriod(this.expiryDate, this.Course) : this.expiryDate,
      isValidUntil: this.isValidUntil,

      isAvailable: this.Assignment.course_available_date.diffNow().as('milliseconds') < 0,
      module: this.Course,
      tokens: this.tokens,
      isExpress: this.Assignment.isExpress,
      token: this.Assignment.token,
    };
  }
}

export interface StudentTrainingCourseData extends BaseModuleInfo, ValidPeriodFields {
  courseID: number;
  course_name: string;
  image: string;
  practical: number;
  minimum_required_course_time_seconds: number;
  addendums: string;
  maximum_course_time: number;
  maximum_course_time_seconds: number;
  expires: Truth;
  expiration_period: number;
  expiration_period_type: string;
  isExpress: boolean;
  token: string;
  auto_assign_retain_month: Truth;
  // Network shows "Valid until" instead of the expiry date
  use_valid_until?: boolean;
}

export class StudentTrainingAssignment {
  max_time_extension: number;
  practical: number;
  status: number;
  calc_percent_complete: number;
  spent_time_seconds: number;
  userID: number;
  hideCourse: number;
  isExpress: boolean;
  token: string;

  @Transform(dateTimeFromSql(), { toClassOnly: true }) date_assigned: DateTime = DateTime.fromJSDate(new Date());
  @Transform(dateTimeFromSql(), { toClassOnly: true }) due_date: DateTime = DateTime.fromJSDate(new Date());
  @Transform(dateTimeFromSql(), { toClassOnly: true }) course_start_date: DateTime = DateTime.fromJSDate(new Date());
  @Transform(dateTimeFromSql(), { toClassOnly: true }) course_end_date: DateTime = DateTime.fromJSDate(new Date());
  @Transform(dateTimeFromSql(), { toClassOnly: true }) course_available_date: DateTime = DateTime.fromJSDate(new Date());
  @Transform(dateTimeFromSql(), { toClassOnly: true }) online_completion_date: DateTime = DateTime.fromJSDate(new Date());
  @Transform(dateTimeFromSql(), { toClassOnly: true }) retained_expiry: DateTime = DateTime.fromJSDate(new Date());
  @Transform(dateTimeFromSql(), { toClassOnly: true }) expiryDate: DateTime = DateTime.fromJSDate(new Date());
}

export interface StudentTrainingData {
  Course: StudentTrainingCourseData;
  Assignment: StudentTrainingAssignment;
  tokens: object;
}

export interface StudentCurriculumVisibilityData {
  StudentsCurriculum: {
    id: number,
    userID: number,
    curriculumID: number,
    hideCurriculum: number
  }
}

export interface StudentSubjectInfo {
  id: number;
  subjectName: string;
  isCurriculum: boolean;
  moduleId: number | null;
  curriculumId: number | null;
  active: boolean | number | string | null;
  studentDeskDisplayValid: boolean;
  studentDeskDisplayExpiry: boolean;
}

export interface StudentSubjectData {
  id: number;
  userId: number;
  subjectId: number;
  dueDate: string | null;
  expiryDate: string | null;
  recurrentDate: string | null;
  validUntil: string | null;
  completed: boolean;
  available: boolean;
  courseIds: number[];
  subjectName: string;
  isCurriculum?: boolean;
  moduleId?: number | null;
  curriculumId?: number | null;
  studentCourseId?: number | null;
  studentCurriculumId?: number | null;
  completionCount?: number;
  recurrenceCount?: number | null;
  assignmentLimitReached?: boolean;
  active?: boolean | number | string | null;
  studentDeskDisplayValid?: boolean | number | string | null;
  studentDeskDisplayExpiry?: boolean | number | string | null;
  subject: StudentSubjectInfo | null;
}

export enum CalendarEventTypes {
  'Module Expiry' = 'ME',
  'Module Complete' = 'MC',
  'Module Due' = 'MD',
  'Personal Event' = 'PE',
  'Offline Expiry' = 'OE',
  'Document Expiry' = 'DE',
}

export enum CalendarEventIcons {
  'M' = 'fa-tag',
  'O' = 'fa-external-link-square-alt',
  'D' = 'fa-briefcase',
  'P' = 'fa-star',
}

export interface EventObject extends Timespan {
  id?: any; // String/number
  title: string;
  allDay?: boolean;
  url?: string;
  className?: string | string[];
  editable?: boolean;
  startEditable?: boolean;
  durationEditable?: boolean;
  rendering?: string;
  overlap?: boolean;
  constraint?: Timespan | BusinessHours;
  source?: EventSource;
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  // non-standard fields, see https://fullcalendar.io/docs/event_data/Event_Object/ and https://fullcalendar.io/docs/event_rendering/eventRender/
  [x: string]: any;
}

export interface Timespan {
  start: string | Date;
  end?: string | Date;
}

export interface BusinessHours {
  start: string | Date;
  end: string | Date;
  dow: number[];
}

export interface UserProfileInfo {
  first_name: string;
  last_name: string;
  userID: number;
}

export interface ExpiringModule {
  Course: {
    courseID: number;
    course_name: string;
    expiration_period: number;
    expiration_period_type: string;
    expiration_type: number;
    round_expiration_date_day: boolean;
    round_expiration_date_up: boolean;
    valid_period_type?: number | string;
    valid_period_length?: number | string;
    valid_period_interval?: string;
    valid_period_after?: number | string;
  }

  StudentsCourse: {
    calc_percent_complete: number;
    courseID: number;
    course_end_date: string;
    retained_expiry: string;
  }

  UserProfile: UserProfileInfo;
}

export interface ExpiringOfflineTraining {
  OfflineCourse: {
    expiration_date: string;
  }

  OfflineCourseName: {
    course_name: string;
  }

  UserProfile: UserProfileInfo;
}

export interface ExpiringDocument {
  DocumentName: {
    document_name: string;
  }

  LicenseAndEndorsement: {
    expiration_date: string;
  }

  UserProfile: UserProfileInfo;
}

export interface DueModule {
  Course: {
    course_name: string;
  }

  StudentsCourse: {
    courseID: number;
    due_date: string;
  }

  UserProfile: UserProfileInfo;
}

export interface RegistrarHomepageCalendarData {
  expiringModules: ExpiringModule[],
  expiringOltes: ExpiringOfflineTraining[],
  expiringDocs: ExpiringDocument[],
  dueModules: DueModule[],
  showModuleValidUntil?: boolean,
}

export interface DateRangeMetricData {
  15: number;
  30: number;
  45: number;
  60: number;
  75: number;
  90: number;
}

export interface RegistrarHomepageData {
  calendarData: RegistrarHomepageCalendarData;
  userLoginData: DateRangeMetricData;
  memoCountData: MemoCountData;
}

export interface StudentsCoursesData {
  courseID: number,
  userID: number,
  date_assigned: DateTime,
  course_end_date: DateTime
}

export interface TrainingData {
  RegistrarHomepageData: RegistrarHomepageData,
  StudentsCoursesData: StudentsCoursesData[],
  TrainingCompletePercent: number,
  TokenInventory: TokenInventoryData[],
  ModuleList: ModuleOption[],
  CurriculumList: Curriculum[]
}

export interface MemoCountData {
  sent: DateRangeMetricData;
  read: DateRangeMetricData;
}

export enum TrainingItemType {
  ModuleDue = 0,
  ModuleExpiring = 1,
  OfflineTrainingExpiring = 2,
  DocumentExpiring = 3,
}

export class TrainingItem {
  name: string;
  type: TrainingItemType;
  studentFirstName: string;
  studentLastName: string;
  studentFullName: string;
  studentId: number;
  date: DateTime;
  percentComplete: number;
  // True on expiring modules when the network shows "valid until" instead of the expiry
  // date; `date` then already holds the valid until date
  isValidUntil?: boolean;
}

/**
 * Shift a module expiry date by the module's valid_period_* rules. Mirrors
 * Course::applyValidPeriod() in cake4 and the logic in competency-report.js.
 */
export interface ValidPeriodFields {
  valid_period_type?: number | string;
  valid_period_length?: number | string;
  valid_period_interval?: string;
  valid_period_after?: number | string;
}

export function applyValidPeriod(expiry: DateTime, course: ValidPeriodFields): DateTime {
  switch (String(course.valid_period_type ?? 0)) {
    case '1':
      return expiry.set({ day: expiry.daysInMonth });

    case '2':
      return expiry.plus({ months: 1 }).set({ day: 1 });

    case '3':
      let length: number = parseInt(String(course.valid_period_length ?? 0), 10) || 0;
      if (parseInt(String(course.valid_period_after ?? 1), 10) !== 1) length = -length;

      switch (course.valid_period_interval) {
        case 'D': return expiry.plus({ days: length });
        case 'W': return expiry.plus({ weeks: length });
        case 'M': return expiry.plus({ months: length });
        case 'Y': return expiry.plus({ years: length });
      }
  }

  return expiry;
}

export class StudentsTrainingData {
  date_assigned: string | DateTime;
  course_end_date: string | DateTime;
  isComplete: boolean = false;

  constructor(data: StudentsCoursesData) {
    this.date_assigned = data.date_assigned;
    this.course_end_date = data.course_end_date;

    this.isComplete = this.course_end_date.toMillis() > 0 ? true : false;
  }
}

export class StudentsTraining {
  modules: Map<number, StudentsTrainingData> = new Map<number, StudentsTrainingData>();
}

export class StudentsCourses {
  training: Map<number, StudentsTraining> = new Map<number, StudentsTraining>();
}

export interface ApiClientData {
  client_id: string;
  client_secret?: string;
  redirect_uri: string;
  companyName: string;
  applicationName: string;
  description: string;
  created: string | DateTime;
  modified: string | DateTime;
  callbackUri: string;
  callbackEnabled: Truth;
  clientCount: number;
  simpleClient: Truth,
  maskedSecret: Truth,
  _startOpen?: boolean;
}

export class ApiClient implements ApiClientData {
  client_id: string = '';
  client_secret?: string = '';
  redirect_uri: string = '';
  companyName: string = '';
  applicationName: string = '';
  description: string = '';
  created: string | DateTime;
  modified: string | DateTime;
  callbackUri: string = '';
  callbackEnabled: Truth = Truth.False;
  clientCount: number;
  simpleClient: Truth = Truth.False;
  maskedSecret: Truth = Truth.True;

  get isNew(): boolean {
    return this.client_id == '';
  }

  constructor() {

  }

}
